import { MapPin, Calendar, Mountain, Leaf } from 'lucide-react'
import type { Tea } from '@/entities/product'

export interface ProductOriginProps {
  /** Чай (для получения информации о происхождении) */
  tea: Tea
}

/**
 * Виджет: Блок происхождения и года урожая
 * 
 * Отображает информацию о происхождении чая:
 * - Страна (originCountry)
 * - Регион (originRegion)  
 * - Ферма (originFarm)
 * - Год урожая (harvestYear)
 * - Сезон сбора (harvestSeason)
 */
export function ProductOrigin({ tea }: ProductOriginProps) {
  const hasCountry = !!tea.originCountry?.trim()
  const hasRegion = !!tea.originRegion?.trim()
  const hasFarm = !!tea.originFarm?.trim()
  const hasYear = tea.harvestYear != null
  const hasSeason = !!tea.harvestSeason
  
  // Если нет данных, не показываем секцию
  if (!hasCountry && !hasRegion && !hasFarm && !hasYear && !hasSeason) {
    return null
  }
  
  // Формируем полное название места происхождения
  const originParts = [tea.originRegion, tea.originCountry].filter(Boolean)
  const originText = originParts.join(', ')
  
  // Перевод сезона
  const seasonLabels: Record<string, string> = {
    spring: 'Весенний сбор',
    summer: 'Летний сбор',
    autumn: 'Осенний сбор',
    winter: 'Зимний сбор',
  }
  
  return (
    <div className="px-4 py-6 space-y-4">
      <h2 className="text-xl font-semibold text-ink dark:text-white">Происхождение</h2>
      
      <div className="grid gap-3 sm:grid-cols-2">
        {/* Регион/Страна */}
        {(hasCountry || hasRegion) && (
          <div className="flex items-start gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <MapPin className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Регион</p>
              <p className="text-base font-medium text-ink dark:text-white">{originText}</p>
            </div>
          </div>
        )}
        
        {/* Ферма */}
        {hasFarm && (
          <div className="flex items-start gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <Mountain className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Ферма</p>
              <p className="text-base font-medium text-ink dark:text-white">{tea.originFarm}</p>
            </div>
          </div>
        )}
        
        {/* Год урожая */}
        {hasYear && (
          <div className="flex items-start gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <Calendar className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Год урожая</p>
              <p className="text-base font-medium text-ink dark:text-white">{tea.harvestYear}</p>
            </div>
          </div>
        )}
        
        {/* Сезон сбора */}
        {hasSeason && (
          <div className="flex items-start gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <Leaf className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Сезон</p>
              <p className="text-base font-medium text-ink dark:text-white">
                {seasonLabels[tea.harvestSeason!] || tea.harvestSeason}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
